import { useState } from "react";
import { LogoLockup } from "./Logos";

const SAFEGUARDING_URL = "http://www.mercycorps.org/who-we-are/ethics-policies";

export type Track = "advisor" | "mentor" | "both";

export function Hero({ onBegin }: { onBegin: (track: Track) => void }) {
  const [track, setTrack] = useState<Track | undefined>();
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | undefined>();

  function handleBegin() {
    if (!track) {
      setError("Please choose how you would like to support: advisor, mentor, or both.");
      return;
    }
    if (!agreed) {
      setError("You must agree to the Mercy Corps Safeguarding Policy to continue.");
      return;
    }
    setError(undefined);
    onBegin(track);
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink flex flex-col">
      <header className="bg-brand-navy">
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-5 md:py-6">
          <LogoLockup />
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-6 py-8 md:py-10">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-brand-teal">
              Open Call, 2026
            </div>
            <h1 className="mt-3 font-bold text-[28px] md:text-[40px] leading-[1.1] text-brand-navy">
              Call for <span className="text-brand-red">Advisors</span> and{" "}
              <span className="text-brand-red">Mentors</span>
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-[15px] md:text-[16px] text-brand-ink/85 leading-relaxed">
              <strong className="text-brand-navy">Gaza Sky Geeks (GSG)</strong>,
              established in 2011 by Mercy Corps with support from Google, works to
              foster sustainable, inclusive, tech-enabled economic development in
              Palestine. Through capacity building, market access, and large-scale
              training in specialized tech skills, GSG strengthens the global
              competitiveness of the Palestinian tech ecosystem. Share your
              experience with the companies and people building it.
            </p>
          </div>

          <div className="mt-8 text-center text-[12px] uppercase tracking-[0.22em] font-bold text-brand-teal">
            Choose how you would like to support
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
            <TrackCard
              active={track === "advisor"}
              onSelect={() => {
                setTrack("advisor");
                setError(undefined);
              }}
              eyebrow="For companies"
              title="Advisor"
              blurb="Work directly with the leadership of Palestinian tech and digital marketing companies."
              points={[
                "Sales and international business development",
                "Go-to-market and strategic planning",
                "Marketing, branding, and communications",
                "Financial management, legal, HR, and talent",
              ]}
            />
            <TrackCard
              active={track === "mentor"}
              onSelect={() => {
                setTrack("mentor");
                setError(undefined);
              }}
              eyebrow="For individuals"
              title="Mentor"
              blurb="Guide Palestinian students and professionals as they grow their skills and careers."
              points={[
                "One to one or group mentorship",
                "Technical and sector expertise",
                "Employability and career development",
                "Soft skills and academic pathways",
              ]}
            />
          </div>

          <div className="mt-4 max-w-4xl mx-auto">
            <button
              type="button"
              onClick={() => {
                setTrack("both");
                setError(undefined);
              }}
              aria-pressed={track === "both"}
              className={
                "w-full text-left rounded-2xl border-2 bg-white shadow-card px-5 py-4 transition flex items-center justify-between gap-4 " +
                (track === "both"
                  ? "border-brand-teal ring-2 ring-brand-teal/25"
                  : "border-brand-line hover:border-brand-teal/60")
              }
            >
              <div className="flex-1">
                <span className="font-bold text-brand-navy text-[16px] md:text-[17px]">
                  Open to both
                </span>
                <span className="ml-2 text-[14px] text-brand-ink/80">
                  I am happy to advise companies and mentor individuals.
                </span>
              </div>
              <span
                aria-hidden
                className={
                  "inline-flex h-6 w-6 items-center justify-center rounded-full border-2 text-[13px] font-bold transition shrink-0 " +
                  (track === "both"
                    ? "border-brand-teal bg-brand-teal text-white"
                    : "border-brand-line text-transparent")
                }
              >
                ✓
              </span>
            </button>
          </div>

          <div className="mt-6 md:mt-8 mx-auto max-w-3xl rounded-2xl bg-white border border-brand-line shadow-card px-5 py-4 md:px-6 md:py-5">
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
              <label className="flex items-start gap-3 cursor-pointer select-none flex-1">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => {
                    setAgreed(e.target.checked);
                    if (e.target.checked) setError(undefined);
                  }}
                  className="mt-0.5 h-5 w-5 rounded border-brand-line accent-brand-red shrink-0"
                />
                <span className="text-[13.5px] text-brand-ink leading-snug">
                  I agree to the{" "}
                  <a
                    className="text-brand-teal underline"
                    href={SAFEGUARDING_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Mercy Corps Ethics and Safeguarding policies
                  </a>
                  .
                </span>
              </label>
              <button
                type="button"
                onClick={handleBegin}
                className="ac-btn-primary text-[15px] px-6 py-3 whitespace-nowrap"
              >
                {track === "mentor"
                  ? "Begin mentor application"
                  : track === "advisor"
                    ? "Begin advisor application"
                    : track === "both"
                      ? "Begin combined application"
                      : "Begin application"}
                <span aria-hidden className="ml-1">→</span>
              </button>
            </div>
            {error && (
              <p className="mt-2 text-[13px] text-brand-red font-semibold">{error}</p>
            )}
          </div>

          <p className="mt-4 text-center text-[12px] text-brand-mute">
            About five minutes. Your responses are confidential and used only by
            the GSG team.
          </p>
        </div>
      </main>
    </div>
  );
}

function TrackCard({
  active,
  onSelect,
  eyebrow,
  title,
  blurb,
  points,
}: {
  active: boolean;
  onSelect: () => void;
  eyebrow: string;
  title: string;
  blurb: string;
  points: string[];
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        "text-left rounded-2xl border-2 bg-white shadow-card px-5 py-5 md:px-6 md:py-6 transition " +
        (active
          ? "border-brand-teal ring-2 ring-brand-teal/25"
          : "border-brand-line hover:border-brand-teal/60")
      }
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brand-teal">
          {eyebrow}
        </div>
        <span
          aria-hidden
          className={
            "inline-flex h-6 w-6 items-center justify-center rounded-full border-2 text-[13px] font-bold transition " +
            (active
              ? "border-brand-teal bg-brand-teal text-white"
              : "border-brand-line text-transparent")
          }
        >
          ✓
        </span>
      </div>
      <div className="mt-1.5 text-[22px] md:text-[24px] font-bold text-brand-navy">
        {title}
      </div>
      <p className="mt-1.5 text-[14px] text-brand-ink/85 leading-relaxed">{blurb}</p>
      <ul className="mt-3 space-y-1.5">
        {points.map((p) => (
          <li
            key={p}
            className="flex items-start gap-2 text-[13.5px] text-brand-ink leading-snug"
          >
            <span
              aria-hidden
              className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-teal shrink-0"
            />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}
